import { filter, groupBy, isEmpty, noop } from 'lodash';
import { useMemo } from 'react';
import { useChatContext } from 'src/chat/hooks/useChatContext';
import { ConsumerGroupOrderFieldsFragment, User } from 'src/gqlReactTypings.generated.d';
import { useModal } from 'src/shared/hooks/useModal';

const DEFAULT_USE_MESSAGING_HOOK = {
  hideMessageModal: noop,
  isMessageModalShowing: false,
  onMessageButtonClick: noop,
  openChatWidget: noop,
  shouldShowMessageButton: false,
};

interface UseMessagingHook {
  hideMessageModal: () => void;
  isMessageModalShowing: boolean;
  onMessageButtonClick: () => void;
  openChatWidget: (recipient: Pick<User, 'flakeId' | 'publicName'>) => void;
  shouldShowMessageButton: boolean;
}

export const useMessaging = (groupOrder: ConsumerGroupOrderFieldsFragment): UseMessagingHook => {
  const { openWidget: openChatWidget } = useChatContext();
  const { isShowing, hide, show } = useModal();
  const { orders } = groupOrder;

  return useMemo(() => {
    if (!openChatWidget) {
      return DEFAULT_USE_MESSAGING_HOOK;
    }

    // If there's just one shef, skip the "Choose the shef to message modal"
    const numShefs = Object.keys(groupBy(orders, 'shefId')).length;
    if (numShefs === 1) {
      const firstOrder = orders[0];
      const canMessageFirstOrder = firstOrder.shefProfile.allowedToChat && orders.length === 1;

      return {
        hideMessageModal: noop,
        isMessageModalShowing: false,
        onMessageButtonClick: canMessageFirstOrder ? () => openChatWidget(firstOrder.publicShef) : noop,
        shouldShowMessageButton: canMessageFirstOrder,
        openChatWidget,
      };
    }

    const ordersWithMessageableShefs = filter(orders, ({ shefProfile }) => shefProfile.allowedToChat);
    if (isEmpty(ordersWithMessageableShefs)) {
      return DEFAULT_USE_MESSAGING_HOOK;
    }

    return {
      hideMessageModal: hide,
      isMessageModalShowing: isShowing,
      onMessageButtonClick: show,
      shouldShowMessageButton: true,
      openChatWidget: (recipient: Pick<User, 'flakeId' | 'publicName'>) => {
        openChatWidget(recipient);
        hide();
      },
    };
  }, [hide, isShowing, openChatWidget, orders, show]);
};
